import React from 'react';
import {
  Button,
  TextField,
  Checkbox,
  IconButton,
  Typography,
  Grid,
  Box,
  Fab,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const useStyles = makeStyles((theme) => ({
  card_container: {
    height: '70px',
    display: 'flex',
    borderRadius: '5px',
    border: `1px solid ${theme.palette.divider}`,
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    '&:hover': {
      border: `1px solid ${theme.palette.accents.dark}`,
      boxShadow: `0 0 10px 0 #376363`,
      transition: 'all 0.3s ease',
    },
  },

  main_content_container: {
    flexGrow: 1,
    padding: theme.spacing(1),
  },

  addSection: {
    padding: '8px 92px',
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: '#494949',
    margin: '4px 12px 18px 12px',
  },

  limitation_title: {
    fontWeight: 'bold',
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.1rem',
    textTransform: 'capitalize',
    color: theme.palette.texts.primary,
  },
  limitation_description: {
    fontSize: theme.fonts.responsiveSmall,
    letterSpacing: '0.1rem',
    color: theme.palette.texts.secondary,
  },

  checkbox_container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },

  fab: {
    position: 'absolute',
    right: 0,

    backgroundColor: '#477777b8',
    color: 'white',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  fabContainer: {
    position: 'relative',
    height: '38px',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },

  cancelButton: {
    color: 'white',
    height: '24px',
    margin: '4px',
    fontSize: 'min(1vh, 0.9rem)',
    minWidth: '80px',
    minHeight: '24px',
    border: '1px solid #477777b8',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },

  addButton: {
    color: 'white',
    height: '24px',
    margin: '4px',
    fontSize: 'min(1vh, 0.9rem)',
    minWidth: '80px',
    minHeight: '24px',
    backgroundColor: '#425f5f',
    '&:hover': {
      backgroundColor: theme.palette.accents.main,
      boxShadow: '0px 0px 5px 0px #d7c2c2',
    },
  },
}));

export default function LimitationsContent({
  limitations,
  newTitle,
  newDescription,
  showAddSection,
  handleToggle,
  handleDelete,
  handleAdd,
  handleToggleAddSection,
  setNewTitle,
  setNewDescription,
  resetAddForm,
  variant = 'card',
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  const renderAddSection = () => {
    if (!showAddSection) return null;

    if (variant === 'card') {
      // Card variant: show add section in a Dialog
      return (
        <Dialog maxWidth='md' open={showAddSection} onClose={resetAddForm}>
          <DialogTitle>{t('Limitations.add_custom_limitation')}</DialogTitle>
          <DialogContent>
            <Box className={classes.addSection}>
              <TextField
                fullWidth
                size='small'
                label={t('Limitations.title')}
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAdd();
                  }
                }}
                margin='normal'
                variant='outlined'
                required
                autoFocus
              />
              <TextField
                fullWidth
                size='small'
                label={t('Limitations.description')}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                margin='normal'
                variant='outlined'
                minRows={1}
              />
              <Box style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                <Button
                  variant='outlined'
                  size='small'
                  className={classes.cancelButton}
                  onClick={resetAddForm}>
                  {t('export.cancel')}
                </Button>
                <Button
                  variant='contained'
                  size='small'
                  className={classes.addButton}
                  startIcon={<Add />}
                  onClick={handleAdd}
                  disabled={!newTitle.trim()}>
                  {t('Limitations.add_limitation')}
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      );
    } else {
      // Dialog variant: show add section inline
      return (
        <Box className={classes.addSection}>
          <Typography variant='subtitle1' gutterBottom>
            {t('Limitations.add_custom_limitation')}
          </Typography>
          <TextField
            fullWidth
            size='small'
            label={t('Limitations.title')}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleAdd();
              }
            }}
            margin='normal'
            variant='outlined'
            required
            autoFocus
          />
          <TextField
            fullWidth
            size='small'
            label={t('Limitations.description')}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            margin='normal'
            variant='outlined'
            multiline
            minRows={1}
          />
          <Box style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
            <Button variant='outlined' size='small' className={classes.cancelButton} onClick={resetAddForm}>
              {t('export.cancel')}
            </Button>
            <Button
              variant='contained'
              size='small'
              className={classes.addButton}
              startIcon={<Add />}
              onClick={handleAdd}
              disabled={!newTitle.trim()}>
              {t('Limitations.add_limitation')}
            </Button>
          </Box>
        </Box>
      );
    }
  };

  return (
    <>
      <Grid
        container
        spacing={variant === 'card' ? 1 : 0}
        style={{ padding: variant === 'card' ? '8px' : '0', width: '100%' }}>
        {limitations.map((lim) => (
          <Grid item xs={12} sm={6} md={4} key={lim.id} style={{ padding: '4px' }}>
            <div
              className={classes.card_container}
              onClick={() => handleToggle(lim.id)}
              style={{
                minHeight: 'max-content',
                backgroundColor: lim.checked ? '#465053' : 'inherit',
                boxShadow: lim.checked && 'rgb(33 44 49 / 58%) 0px 0px 3px 1px',
              }}>
              <div className={classes.main_content_container}>
                <Typography
                  className={classes.limitation_title}
                  style={{
                    marginBottom: variant === 'dialog' ? '8px' : '0',
                  }}>
                  {lim.title}
                </Typography>
                {lim.description && (
                  <Typography className={classes.limitation_description} color='textSecondary'>
                    {lim.description}
                  </Typography>
                )}
              </div>
              <div
                className={classes.checkbox_container}
                onClick={(e) => {
                  e.stopPropagation();
                }}>
                {lim.custom ? (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(lim.id);
                    }}
                    aria-label={`Delete ${lim.title}`}
                    size='small'>
                    <Delete fontSize='small' />
                  </IconButton>
                ) : (
                  <Checkbox
                    size='small'
                    checked={lim.checked}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggle(lim.id);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    color='secondary'
                    inputProps={{ 'aria-label': `Toggle ${lim.title}` }}
                  />
                )}
              </div>
            </div>
          </Grid>
        ))}
      </Grid>

      {/* FAB to toggle add section */}
      <Box className={classes.fabContainer} style={{ width: variant === 'card' ? '98%' : '100%' }}>
        <Fab
          aria-label='add limitation'
          onClick={handleToggleAddSection}
          className={classes.fab}
          size='small'
          style={{
            bottom: variant === 'card' ? '10px' : '0',
            height: variant === 'card' ? '26px' : '30px',
            width: variant === 'card' ? '26px' : '30px',
            minWidth: variant === 'card' ? '26px' : '30px',
            minHeight: variant === 'card' ? '26px' : '30px',
          }}>
          {showAddSection ? <ExpandLessIcon fontSize='small' /> : <Add fontSize='small' />}
        </Fab>
      </Box>

      {/* Add custom limitation section */}
      {renderAddSection()}
    </>
  );
}
